import { SVGProps } from 'react';
import { useRouter } from 'next/router';
import { useTranslation } from 'next-i18next';
import { FaceFillIcon } from '@zep/icons';
import { Accordion, AccordionButton, AccordionPanel, Spacing } from '@zep/ui';
import { Button } from '@zep/ui/experimental';
import { isMobile } from '@zep/utils/device';
import { PricingCardProps } from '../../types';
import { PricingCard } from '../PricingCard';
import { FeatureListItem } from './shared';
export const ProPlanCard = (props: PricingCardProps) => {
  const {
    t
  } = useTranslation(['subscription']);
  const {
    locale,
    push
  } = useRouter();
  return <PricingCard.Root className="relative border-2 border-[#994AFF] shadow-[0_10px_18px_4px_rgba(42,39,65,0.22)]" data-sentry-element="unknown" data-sentry-component="ProPlanCard" data-sentry-source-file="ProPlanCard.tsx">
      <div className="absolute left-1/2 top-[-10px] flex w-max -translate-x-1/2 items-center gap-[2px] rounded-[4px] bg-gradient-to-r from-primary to-[#994AFF] px-[6px] py-[2px]">
        <span className="text-caption-1 font-[600] text-white">
          {t('subscription:pricing.pro.tag')}
        </span>
        <FaceFillIcon width={14} height={14} fill="#fff" data-sentry-element="FaceFillIcon" data-sentry-source-file="ProPlanCard.tsx" />
      </div>

      <PricingCard.Header data-sentry-element="unknown" data-sentry-source-file="ProPlanCard.tsx">
        <div className="flex">
          <div className="flex w-full flex-col">
            <PricingCard.Title data-sentry-element="unknown" data-sentry-source-file="ProPlanCard.tsx">Pro</PricingCard.Title>
            <PricingCard.TitleDescription data-sentry-element="unknown" data-sentry-source-file="ProPlanCard.tsx">
              {t('subscription:pricing.pro.description')}
            </PricingCard.TitleDescription>
          </div>
          <PricingCard.Logo data-sentry-element="unknown" data-sentry-source-file="ProPlanCard.tsx">
            <BoltFillIconSvg data-sentry-element="BoltFillIconSvg" data-sentry-source-file="ProPlanCard.tsx" />
          </PricingCard.Logo>
        </div>
      </PricingCard.Header>
      <PricingCard.Content className="flex flex-col items-start" data-sentry-element="unknown" data-sentry-source-file="ProPlanCard.tsx">
        <PricingCard.Price pricePerUser={props.pricePerUser ?? 0} data-sentry-element="unknown" data-sentry-source-file="ProPlanCard.tsx" />
        <Spacing size={14} data-sentry-element="Spacing" data-sentry-source-file="ProPlanCard.tsx" />
        <Button className="h-[40px] w-full" onClick={props.onClickCardButton} data-sentry-element="Button" data-sentry-source-file="ProPlanCard.tsx">
          {t('subscription:pricing.startButton')}
        </Button>
        {locale === 'ko' && <div className="w-full">
            <Spacing size={6} />
            <Button size="xs" intent="sub" variant="transparent" className="h-[30px] w-full" onClick={() => {
          void push({
            pathname: '/pricing/quote',
            query: {
              plan: 'pro',
              period: props.coreQuotePeriod ?? '1M'
            }
          });
        }}>
              {t('subscription:pricing.12734dd4')}
            </Button>
          </div>}
      </PricingCard.Content>
      <PricingCard.Footer data-sentry-element="unknown" data-sentry-source-file="ProPlanCard.tsx">
        {isMobile() ? <Accordion className="!border-none !bg-white">
            <AccordionButton className="text-body-1-sb justify-between !p-0 !text-[14px] font-[600] text-gray-900">
              {t('subscription:pricing.mainFeatures')}
            </AccordionButton>
            <AccordionPanel className="!p-0">
              <Spacing size={16} />
              <FooterContent />
              <Spacing size={14} />
            </AccordionPanel>
          </Accordion> : <FooterContent />}
      </PricingCard.Footer>
    </PricingCard.Root>;
};
const FooterContent = () => {
  const {
    t
  } = useTranslation(['subscription']);
  const {
    locale
  } = useRouter();
  const isLocaleJapanese = locale === 'ja';
  return <div className="flex flex-col" data-sentry-component="FooterContent" data-sentry-source-file="ProPlanCard.tsx">
      <PricingCard.FeatureTitle data-sentry-element="unknown" data-sentry-source-file="ProPlanCard.tsx">
        {t('subscription:pricing.pro.feature1')}
      </PricingCard.FeatureTitle>
      <Spacing size={16} data-sentry-element="Spacing" data-sentry-source-file="ProPlanCard.tsx" />
      <FeatureListItem.Root data-sentry-element="unknown" data-sentry-source-file="ProPlanCard.tsx">
        <FeatureListItem.List className="items-start" data-sentry-element="unknown" data-sentry-source-file="ProPlanCard.tsx">
          <FeatureListItem.Icon data-sentry-element="unknown" data-sentry-source-file="ProPlanCard.tsx" />
          <FeatureListItem.Text data-sentry-element="unknown" data-sentry-source-file="ProPlanCard.tsx">
            {t('subscription:pricing.pro.feature5')}{' '}
            <a href={t('subscription:pricing.pro.seeDetailsLink')} className="text-body-2-m font-[500] text-[#0D94FF]" target="_blank">
              {t('subscription:pricing.pro.seeDetails')}
            </a>
          </FeatureListItem.Text>
        </FeatureListItem.List>
        <FeatureListItem.List data-sentry-element="unknown" data-sentry-source-file="ProPlanCard.tsx">
          <FeatureListItem.Icon data-sentry-element="unknown" data-sentry-source-file="ProPlanCard.tsx" />
          <FeatureListItem.Text data-sentry-element="unknown" data-sentry-source-file="ProPlanCard.tsx">
            {t('subscription:pricing.pro.feature2')}
          </FeatureListItem.Text>
        </FeatureListItem.List>
        <FeatureListItem.List data-sentry-element="unknown" data-sentry-source-file="ProPlanCard.tsx">
          <FeatureListItem.Icon data-sentry-element="unknown" data-sentry-source-file="ProPlanCard.tsx" />
          <FeatureListItem.Text data-sentry-element="unknown" data-sentry-source-file="ProPlanCard.tsx">
            {t('subscription:pricing.pro.feature4')}
          </FeatureListItem.Text>
        </FeatureListItem.List>
        {!isLocaleJapanese && <FeatureListItem.List>
            <FeatureListItem.Icon />
            <FeatureListItem.Text>
              {t('subscription:pricing.enterprise.feature2')}
            </FeatureListItem.Text>
          </FeatureListItem.List>}
        <FeatureListItem.List data-sentry-element="unknown" data-sentry-source-file="ProPlanCard.tsx">
          <FeatureListItem.Icon data-sentry-element="unknown" data-sentry-source-file="ProPlanCard.tsx" />
          <FeatureListItem.Text data-sentry-element="unknown" data-sentry-source-file="ProPlanCard.tsx">
            {t('subscription:pricing.pro.visitHistoryDownload')}
          </FeatureListItem.Text>
        </FeatureListItem.List>
        <FeatureListItem.List data-sentry-element="unknown" data-sentry-source-file="ProPlanCard.tsx">
          <FeatureListItem.Icon data-sentry-element="unknown" data-sentry-source-file="ProPlanCard.tsx" />
          <FeatureListItem.Text data-sentry-element="unknown" data-sentry-source-file="ProPlanCard.tsx">
            {t('subscription:pricing.pro.messagePermission')}
          </FeatureListItem.Text>
        </FeatureListItem.List>
        <FeatureListItem.List data-sentry-element="unknown" data-sentry-source-file="ProPlanCard.tsx">
          <FeatureListItem.Icon data-sentry-element="unknown" data-sentry-source-file="ProPlanCard.tsx" />
          <FeatureListItem.Text data-sentry-element="unknown" data-sentry-source-file="ProPlanCard.tsx">
            {t('subscription:pricing.pro.spotlightParticipantVideo')}
          </FeatureListItem.Text>
        </FeatureListItem.List>
        <FeatureListItem.List data-sentry-element="unknown" data-sentry-source-file="ProPlanCard.tsx">
          <FeatureListItem.Icon data-sentry-element="unknown" data-sentry-source-file="ProPlanCard.tsx" />
          <FeatureListItem.Text data-sentry-element="unknown" data-sentry-source-file="ProPlanCard.tsx">
            {t('subscription:pricing.pro.connectionLostAlarm')}
          </FeatureListItem.Text>
        </FeatureListItem.List>
      </FeatureListItem.Root>
    </div>;
};
const BoltFillIconSvg = (props: SVGProps<SVGSVGElement>) => <svg xmlns="http://www.w3.org/2000/svg" width={28} height={28} fill="none" {...props} data-sentry-element="svg" data-sentry-component="BoltFillIconSvg" data-sentry-source-file="ProPlanCard.tsx">
    <path fill="url(#bolt-fill)" fillRule="evenodd" d="M15.528 2.39c.48.157.806.605.806 1.11v7h5.833a1.167 1.167 0 0 1 .943 1.853l-9.333 12.833a1.167 1.167 0 0 1-2.11-.686v-7H5.834a1.167 1.167 0 0 1-.944-1.853l9.333-12.833c.298-.409.824-.58 1.305-.423Z" clipRule="evenodd" data-sentry-element="path" data-sentry-source-file="ProPlanCard.tsx" />
    <defs data-sentry-element="defs" data-sentry-source-file="ProPlanCard.tsx">
      <linearGradient id="bolt-fill" x1={14} x2={14} y1={2.333} y2={25.667} gradientUnits="userSpaceOnUse" data-sentry-element="linearGradient" data-sentry-source-file="ProPlanCard.tsx">
        <stop stopColor="#6758FF" data-sentry-element="stop" data-sentry-source-file="ProPlanCard.tsx" />
        <stop offset={1} stopColor="#93A4F1" data-sentry-element="stop" data-sentry-source-file="ProPlanCard.tsx" />
      </linearGradient>
    </defs>
  </svg>;